import { useMemo } from 'react'
import { useAtom } from 'jotai'
import { WorkspaceType } from '@deepguide-ai/types'
import {
  getWorkspaceTypeConfig,
  getWorkspaceTypeIcon,
  getWorkspaceTypeIconColor,
  type WorkspaceTypeConfig,
} from '@/config/workspace-types'
import { activeWorkspaceWithStorageAtom } from '@/stores/workspaceStore'

/**
 * Hook to access workspace type configuration and utilities
 *
 * @example
 * ```tsx
 * const { type, config, icon: Icon, iconColor, isCompliance } = useWorkspaceType()
 *
 * return (
 *   <div>
 *     <Icon className={iconColor} />
 *     <h1>{config.label}</h1>
 *     {isCompliance && <ComplianceFeatures />}
 *   </div>
 * )
 * ```
 */
export function useWorkspaceType() {
  const [workspace] = useAtom(activeWorkspaceWithStorageAtom)

  const type: WorkspaceType = useMemo(
    () => workspace?.type || 'DOCUMENTATION',
    [workspace?.type]
  )

  const config: WorkspaceTypeConfig = useMemo(
    () => getWorkspaceTypeConfig(type),
    [type]
  )

  const icon = useMemo(() => getWorkspaceTypeIcon(type), [type])

  const iconColor = useMemo(() => getWorkspaceTypeIconColor(type), [type])

  // Convenience boolean flags for common checks.
  // SCREENATA workspaces are compliance workspaces (Screenata is the new
  // brand for the COMPLIANCE product), so isCompliance is true for both.
  const isDocumentation = type === 'DOCUMENTATION'
  const isCompliance = type === 'COMPLIANCE' || type === 'SCREENATA'
  const isTraining = type === 'TRAINING'
  const isSupport = type === 'SUPPORT'
  const isApiDocs = type === 'API_DOCS'
  const isRunbooks = type === 'RUNBOOKS'

  // Check if specific features are enabled
  const hasComplianceFeatures = useMemo(
    () =>
      isCompliance ||
      workspace?.enabledFeatures?.includes('COMPLIANCE_AUTOMATION') ||
      (workspace?.enabledFrameworks?.length ?? 0) > 0,
    [isCompliance, workspace?.enabledFeatures, workspace?.enabledFrameworks]
  )

  const hasApiDocsFeatures = useMemo(
    () =>
      isApiDocs ||
      workspace?.enabledFeatures?.includes('API_DOCUMENTATION'),
    [isApiDocs, workspace?.enabledFeatures]
  )

  const hasTrainingFeatures = useMemo(
    () =>
      isTraining ||
      workspace?.enabledFeatures?.includes('VIDEO_TUTORIALS'),
    [isTraining, workspace?.enabledFeatures]
  )

  const hasKnowledgeBase = useMemo(
    () =>
      isSupport ||
      isTraining ||
      workspace?.enabledFeatures?.includes('KNOWLEDGE_BASE'),
    [isSupport, isTraining, workspace?.enabledFeatures]
  )

  const hasWorkflowAutomation = useMemo(
    () =>
      isRunbooks ||
      workspace?.enabledFeatures?.includes('WORKFLOW_AUTOMATION'),
    [isRunbooks, workspace?.enabledFeatures]
  )

  // Terminology shortcuts for easier access
  const terminology = config.terminology

  return {
    // Core properties
    type,
    config,
    icon,
    iconColor,

    // Type boolean flags
    isDocumentation,
    isCompliance,
    isTraining,
    isSupport,
    isApiDocs,
    isRunbooks,

    // Feature checks
    hasComplianceFeatures,
    hasApiDocsFeatures,
    hasTrainingFeatures,
    hasKnowledgeBase,
    hasWorkflowAutomation,

    // Workspace-specific terminology
    terminology,

    // Raw workspace data
    workspace,
    enabledFrameworks: workspace?.enabledFrameworks || [],
    enabledFeatures: workspace?.enabledFeatures || [],
  }
}

/**
 * Hook to check if workspace has a specific type
 *
 * @example
 * ```tsx
 * const isCompliance = useWorkspaceTypeCheck('COMPLIANCE')
 * ```
 */
export function useWorkspaceTypeCheck(typeToCheck: WorkspaceType): boolean {
  const { type } = useWorkspaceType()
  return type === typeToCheck
}

/**
 * Hook to check if workspace has any of the specified types
 *
 * @example
 * ```tsx
 * const isComplianceOrRunbooks = useWorkspaceTypeMatch(['COMPLIANCE', 'RUNBOOKS'])
 * ```
 */
export function useWorkspaceTypeMatch(types: WorkspaceType[]): boolean {
  const { type } = useWorkspaceType()
  return types.includes(type)
}

/**
 * Hook to get workspace type configuration without needing active workspace
 * Useful for components that want to display type info for any workspace
 *
 * @example
 * ```tsx
 * const config = useWorkspaceTypeInfo('COMPLIANCE')
 * ```
 */
export function useWorkspaceTypeInfo(type: WorkspaceType) {
  const config = useMemo(() => getWorkspaceTypeConfig(type), [type])
  const icon = useMemo(() => getWorkspaceTypeIcon(type), [type])
  const iconColor = useMemo(() => getWorkspaceTypeIconColor(type), [type])

  return {
    type,
    config,
    icon,
    iconColor,
  }
}
